<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { useRoute } from 'vue-router';
import { useElementSize } from '@vueuse/core';
import { dateUtils } from '../utils/date-utils';
import { useCoreStore, useUiStore } from '@/stores';
import AppImage from '@/core/components/app-image.vue';
import AppAvatar from '@/core/components/app-avatar.vue';
import { PropType, computed, ref, watch, watchEffect } from 'vue';
import useUserPreferences from '../composable/use-user-preferences';
import useCacheUserAvatars from '../composable/use-cache-user-avatars';
import { dataTablePreset } from '../../presets/customizations/datatable';
import useContainerTableColumns from '../composable/use-container-table-columns';
import AppTableRouterLinkColumn from './common/app-table-router-link-column.vue';
import { getIdFieldPropertyName } from '../../core/ts-decorators/model-decorators';
import { DataTableColumnReorderEvent, DataTableFilterMeta } from 'primevue/datatable';
import { TableBasedContainerData, IColumnChipOptions } from '../../stores/core/models';
import { getPaginationBasedOnUserPreferences } from 'src/core/utils/user-preferences-utils';
import AppDynamicFilterTableColumn from './dynamic-filters/app-dynamic-filter-table-column.vue';
import useContainerDynamicFilters from '../composable/containers/use-container-dynamic-filters';

defineEmits(['sort', 'after-details-navigation']);

const tableHeight = defineModel<Number>('tableHeight');
const { tabsContainerData } = storeToRefs(useCoreStore());
const { getPaginationPreferenceKey } = useUserPreferences();
var containerData = defineModel<TableBasedContainerData>('containerData', { required: true });

const props = defineProps({
  showOnlyFirstColumn: {
    type: Boolean,
  },
  scrollHeight: {
    type: Number,
    default: '400',
  },
  scrollwidth: {
    type: Number,
  },
  detailsRouteIdParamName: {
    type: String as PropType<'id' | 'id2' | 'id3'>,
    default: 'id',
  },
  reorderableColumns: {
    type: Boolean,
    default: true,
  },
  useLastTabsContainerRouteForDetails: {
    type: Boolean,
  },
});

const route = useRoute();
const tableRef = ref(null);
const paginationUserPreference = computed(() =>
  getPaginationBasedOnUserPreferences(getPaginationPreferenceKey(containerData.value.containerType, route))
);
const uiStore = useUiStore();
const { height } = useElementSize(tableRef);
const filters = ref({} as DataTableFilterMeta);
const idPropName = computed(() => getIdFieldPropertyName(containerData.value.options.modelClassName) || 'id');
const { containerDynamicFilterKeys } = useContainerDynamicFilters(containerData.value.containerType);
const { visibleColumns, setColumnPosition } = useContainerTableColumns(computed(() => containerData.value.containerType));

const onColReorder = (e: DataTableColumnReorderEvent) => setColumnPosition(e.dragIndex, e.dropIndex);

const expandedRows = ref({});

useCacheUserAvatars(
  computed(() => containerData.value.options.rows),
  containerData.value.options.columns.filter((x) => x.formatAsAvatar).map((x) => x.field as string)
);

const onRowToggle = (event: { data: {} }) => {
  expandedRows.value = event.data;
};
const initFilters = () => {
  const columnsWithFilters = containerData.value.options.columns
    .filter((x) => !!x.filterKey && !!x.field)
    .map((x) => {
      return { [x.field as string]: { value: null, matchMode: 'contains' } }; // FilterMatchMode.CONTAINS
    });

  filters.value = columnsWithFilters.length
    ? columnsWithFilters.reduce((acc, iter) => {
        return { ...acc, ...iter };
      })
    : {};
};

const isRowSelected = (row: any) => {
  return containerData.value.routeParams[props.detailsRouteIdParamName] == row[idPropName.value];
};

const getChipClass = (getChipOptions: (row: any) => IColumnChipOptions | undefined, row: any) => {
  if (getChipOptions) {
    const chipOpts = getChipOptions(row);

    if (chipOpts) {
      const chipOptsClass = chipOpts.bgColor + ' ' + chipOpts.borderColor + ' ' + chipOpts.textColor;
      return chipOptsClass;
    }
  }

  return '';
};

const syncSelectedRows = () => {
  /*
      Keep in Sync selected rows data with source rows data.
      when data is reloaded the selected rows data must be updated with new rows data.
    */
  var selectedRows = containerData.value.options.rows.filter((x) =>
    containerData.value.selectedKeys.includes(x[idPropName.value as string])
  );

  selectedRows.forEach((x) => {
    if (containerData.value.options.selectionMode == 'multiple') {
      const index = (containerData.value.selectedRows as any[]).findIndex(
        (y) => y[idPropName.value as string] == x[idPropName.value as string]
      );
      if (index >= 0) (containerData.value.selectedRows as any[]).splice(index, 1, x);
    } else {
      if (
        containerData.value.selectedRows &&
        containerData.value.selectedRows[idPropName.value as string] == x[idPropName.value as string]
      )
        containerData.value.selectedRows = x;
    }
  });
};

watchEffect(() => (tableHeight.value = height.value));

watchEffect(() => {
  if (idPropName.value) {
    if (containerData.value.options.selectionMode == 'multiple')
      containerData.value.selectedKeys = (containerData.value.selectedRows as any[]).map((x) => x[idPropName.value as string]);
    else
      containerData.value.selectedKeys = containerData.value.selectedRows[idPropName.value]
        ? [containerData.value.selectedRows[idPropName.value]]
        : [];
  }
});

const tableStyle = computed(() => {
  let style = {};

  if (props.scrollwidth && !props.showOnlyFirstColumn) style = { width: props.scrollwidth + 'px' };

  return style;
});

watch(
  () => containerData.value.options.rows,
  () => {
    syncSelectedRows();
  }
);

watch(
  () => idPropName.value,
  () => (containerData.value.options.idPropertyName = idPropName.value)
);

initFilters();
</script>

<template>
  <DataTable
    lazy
    v-model:filters="filters"
    v-model:expandedRows="expandedRows"
    ref="tableRef"
    :reorderable-columns="props.reorderableColumns"
    @columnReorder="onColReorder"
    @rowToggle="onRowToggle"
    scrollable
    :scrollHeight="props.scrollHeight + 'px'"
    :dataKey="idPropName"
    size="small"
    :value="containerData.options.rows"
    v-model:selection="containerData.selectedRows"
    class="container-table"
    :class="{
      'container-table-hide-horizontal-scrollbar': props.showOnlyFirstColumn,
      'app-table-1st-col-padding': !containerData.options.selectionMode,
    }"
    @sort="$emit('sort', $event)"
    :ptOptions="{ mergeSections: true, mergeProps: true }"
    :pt="dataTablePreset"
    filterDisplay="menu"
    :style="tableStyle"
    removableSort
    :sortField="paginationUserPreference?.sortBy"
    :sortOrder="paginationUserPreference?.descending === true ? -1 : paginationUserPreference?.descending === false ? 1 : 0"
  >
    <Column expander style="width: 4rem" v-if="containerData.options.isRowExpandable" />
    <Column
      class="pl-3"
      :reorderableColumn="false"
      headerStyle="width: 8px"
      :selectionMode="containerData.options.selectionMode"
      v-if="!!containerData.options.selectionMode"
    />
    <Column
      v-for="col of visibleColumns.slice(0, props.showOnlyFirstColumn ? 1 : containerData.options.columns.length)"
      :key="col.field as string"
      :field="col.field"
      :header="col.header ? $t(col.header) : ''"
      :sortable="col.sortable"
      :showFilterOperator="false"
      :showFilterMatchModes="false"
      :show-clear-button="false"
      :show-apply-button="false"
      :filterMenuStyle="{ width: '440px' }"
      :bodyClass="col.class"
    >
      <!----------------------
      -- Details Route Link -- 
      ------------------------>
      <template #body="slotProps" v-if="col.detailsRoute">
        <app-table-router-link-column
          :column="col"
          :row="slotProps.data"
          :details-route-name="
            props.useLastTabsContainerRouteForDetails
              ? tabsContainerData.lastActiveRouteName || col.detailsRoute
              : col.detailsRoute
          "
          :truncate-width="props.showOnlyFirstColumn ? 225 : 0"
          @after-navigate="$emit('after-details-navigation')"
          :details-route-id-param-name="props.detailsRouteIdParamName"
          :is-active="isRowSelected(slotProps.data)"
          :customClass="col.getClass ? col.getClass(slotProps.data) : ''"
        />
      </template>

      <!-----------
      -- Actions --
      ------------->
      <template #body="slotProps" v-else-if="col.formatAsActions">
        <slot name="actions" :row="slotProps.data"></slot>
      </template>

      <!-------------------
      -- Custom Template --
      --------------------->
      <template #body="slotProps" v-else-if="col.useCustomTemplate">
        <slot
          name="custom"
          :row="col.formatField ? col.formatField(slotProps.data) : slotProps.data"
          :rowData="slotProps.data"
          :col="col"
        ></slot>
      </template>

      <!--------------------
      -- Custom Template2 --
      ---------------------->
      <template #body="slotProps" v-else-if="col.useCustomTemplate2">
        <slot name="custom2" :row="slotProps.data"></slot>
      </template>

      <!--------------------
      -- Custom Template3 --
      ---------------------->
      <template #body="slotProps" v-else-if="col.useCustomTemplate3">
        <slot name="custom3" :row="slotProps.data"></slot>
      </template>

      <!----------
      -- Avatar -- 
      ------------>
      <template #body="slotProps" v-else-if="col.formatAsAvatar">
        <div :class="col.class">
          <app-avatar
            :avatar="uiStore.getUserAvatarFromCache(slotProps.data[col.field as string])"
            v-if="slotProps.data[col.field as string]"
          />
        </div>
      </template>

      <!----------
      -- Images -- 
      ------------>
      <template #body="slotProps" v-else-if="col.formatAsImage">
        <div :class="col.class">
          <app-image :base64="slotProps.data[col.field as string]" />
        </div>
      </template>

      <!---------------
      -- Date Column -- 
      ----------------->
      <template #body="slotProps" v-else-if="col.formatAsDateTime">
        <span v-if="col.field" :class="col.class">
          {{ dateUtils.formatUtcToLocalTimeFormat(slotProps.data[col.field as string]) }}
        </span>
      </template>

      <!---------------------------
      -- Formated table row cell -- 
      ----------------------------->
      <template #body="slotProps" v-else-if="!!col.formatField">
        <div
          class="flex items-center whitespace-nowrap overflow-hidden overflow-ellipsis gap-1"
          :class="col.getClass ? col.getClass(slotProps.data) : ''"
          v-tooltip.bottom="col.getTooltip ? col.getTooltip(slotProps.data) : ''"
          :type="col.getTooltip ? 'text' : ''"
        >
          <Icon v-if="col.getIcon" :icon="col.getIcon(slotProps.data)" />
          <img :src="col.getIconImg(slotProps.data)" v-if="col.getIconImg" :class="col.getIconImg(slotProps.data)" />
          <span
            :class="getChipClass(col.getChipOptions, slotProps.data)"
            class="w-fit border text-[12px] px-[8px] py-[1px] rounded-2xl flex align-items-center justify-content-center"
            v-if="!!col.getChipOptions && col.formatField && slotProps.data"
          >
            {{ col.formatField(slotProps.data) }}
          </span>
          <span v-else-if="col.formatField">
            {{ col.formatField(slotProps.data) }}
          </span>
        </div>
      </template>

      <!-- <template #body="slotProps" v-else> col {{ col }} </template> -->

      <!----------------
      -- Filters Body --
      ------------------>
      <template #filter="{ applyFilter }" v-if="!!col.filterKey">
        <app-dynamic-filter-table-column
          @hide-menu="applyFilter()"
          :filter-key="col.filterKey"
          :container-type="containerData.containerType"
          :options="containerData.dynamicFilterOptions"
        />
      </template>

      <template #filtericon v-if="containerDynamicFilterKeys.includes(col.filterKey || '')">
        <Icon icon="hugeicons:menu-08" />
      </template>
    </Column>
    <!----------------
    --Expandable Row--
    ----------------->
    <template #expansion="slotProps" v-if="containerData.options.isRowExpandable">
      <div class="p-2">
        {{ slotProps.data[containerData.options.expandRowFieldName] }}
      </div>
    </template>
  </DataTable>
</template>

<style>
[data-pc-name='pcheadercheckbox'] [data-pc-section='box'],
[data-pc-name='pcrowcheckbox'] [data-pc-section='box'] {
  border-width: 1px !important;
}

.dark [data-pc-name='pcheadercheckbox'] [data-pc-section='box'],
.dark [data-pc-name='pcrowcheckbox'] [data-pc-section='box'] {
  border-width: 1px !important;
  background-color: #1d2939;
}

[data-pc-name='pcrowcheckbox'] [data-pc-section='box'] svg,
[data-pc-name='pcheadercheckbox'] [data-pc-section='box'] svg {
  transform: translate(2px, 2px);
  color: #155eef;
  stroke-width: 1px; /* Makes the tick bolder */
  stroke: currentColor;
  fill: currentColor;
}

/* Hover styles for checked state - DataTable row checkboxes */
input[type='checkbox']:checked:hover + [data-pc-section='box'],
input[type='checkbox'][checked]:hover + [data-pc-section='box'] {
  background-color: #f6f8fb !important; /* gray-200 */
}

/* Hover styles for checked state - DataTable header checkbox */
[data-pc-name='pcheadercheckbox'] input[type='checkbox']:checked:hover + [data-pc-section='box'],
[data-pc-name='pcheadercheckbox'] input[type='checkbox'][checked]:hover + [data-pc-section='box'] {
  background-color: #e5e7eb !important; /* gray-200 */
}

/* Dark mode hover styles */
.dark input[type='checkbox']:checked:hover + [data-pc-section='box'],
.dark input[type='checkbox'][checked]:hover + [data-pc-section='box'] {
  background-color: #4b5563 !important; /* gray-600 */
}

/* Dark mode hover styles for header checkbox */
.dark [data-pc-name='pcheadercheckbox'] input[type='checkbox']:checked:hover + [data-pc-section='box'],
.dark [data-pc-name='pcheadercheckbox'] input[type='checkbox'][checked]:hover + [data-pc-section='box'] {
  background-color: #4b5563 !important; /* gray-600 */
}
</style>
